// Reine Regeln des AclEditors (DESIGN §3.4.10): mindestens ein Manager, keine Dubletten, geerbte
// Einträge sind nicht änderbar.

export type AclRole = 'viewer' | 'editor' | 'manager';
export type AclSubjectKind = 'company' | 'department' | 'team' | 'role' | 'person';

export interface AclSubject {
  /** Stabile ID des Subjekts, z. B. Personen- oder Team-ID. */
  id: string;
  kind: AclSubjectKind;
  name: string;
  /** Unterzeile in der Suche, z. B. „Engineering Lead · Credit Cards“. */
  description?: string;
}

export interface AclEntry extends AclSubject {
  role: AclRole;
  /** Herkunft geerbter Einträge („Policies“); solche Zeilen sind grau und nicht änderbar. */
  inheritedFrom?: string;
}

export const ACL_ROLES: { value: AclRole; label: string }[] = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'manager', label: 'Manager' },
];

export const aclKey = (subject: Pick<AclSubject, 'kind' | 'id'>) => `${subject.kind}:${subject.id}`;

/** Manager über alle wirksamen Einträge (eigene und, falls geerbt wird, die geerbten). */
export const managerCount = (entries: AclEntry[]) =>
  entries.filter((entry) => entry.role === 'manager').length;

/** Darf diese Zeile entfernt bzw. herabgestuft werden, ohne dass kein Manager übrig bleibt? */
export function isLastManager(entry: AclEntry, effective: AclEntry[]): boolean {
  return entry.role === 'manager' && managerCount(effective) <= 1;
}

/** Fügt ein Subjekt als Viewer hinzu; vorhandene eigene Einträge bleiben unverändert. */
export function addSubject(
  entries: AclEntry[],
  subject: AclSubject,
  role: AclRole = 'viewer',
): AclEntry[] {
  if (entries.some((entry) => aclKey(entry) === aclKey(subject))) return entries;
  return [...entries, { ...subject, role }];
}

export function setRole(entries: AclEntry[], key: string, role: AclRole): AclEntry[] {
  return entries.map((entry) => (aclKey(entry) === key ? { ...entry, role } : entry));
}

export function removeEntry(entries: AclEntry[], key: string): AclEntry[] {
  return entries.filter((entry) => aclKey(entry) !== key);
}
